import React from "react";
import {Button} from "../../components/core/Button.jsx";
import {Icon} from "../../components/core/Icon.jsx";
import {Card} from "../../components/core/Card.jsx";
import {Badge} from "../../components/core/Badge.jsx";
import {Input} from "../../components/forms/Input.jsx";
import {Select} from "../../components/forms/Select.jsx";
import {Checkbox} from "../../components/forms/Checkbox.jsx";
import {Tabs} from "../../components/navigation/Tabs.jsx";
import {Toast} from "../../components/feedback/Toast.jsx";
import {H2,Section,Eyebrow,Glitch,Reveal,Photo} from "./Shell.jsx";
import {AgentDiagram} from "./AgentDiagram.jsx";
import {AgentFlowModal} from "./AgentFlowModal.jsx";

/* ---------- Servicios ---------- */
const MATERIALS=[
  ["Zirconio multicapa","Coronas, puentes hasta 14 unidades","3-5 días"],
  ["Disilicato de litio","Carillas, incrustaciones","4 días"],
  ["PMMA","Provisionales de larga duración","2 días"],
  ["Cr-Co fresado","Estructuras sobre implante","5-7 días"],
  ["Resina impresa","Modelos, férulas, guías","24-48 h"]
];

export function ServicesScreen({go}){
  const [tab,setTab]=React.useState("materiales");
  return <>
    <Section>
      <div className="dp-split" style={{gap:"clamp(32px,5vw,64px)",alignItems:"center"}}>
        <div>
          <Eyebrow>Servicios</Eyebrow>
          <h1 style={{fontSize:"clamp(32px,5.4vw,48px)",marginTop:18,maxWidth:"20ch"}}><Glitch>Materiales, plazos y cómo trabajo cada caso en el laboratorio</Glitch></h1>
          <p style={{fontSize:"clamp(17px,2vw,20px)",lineHeight:1.45,color:"var(--fg-2)",marginTop:20,maxWidth:"52ch"}}>Los plazos son reales, contados desde que tengo el escaneo validado. Si un caso no entra en plazo, te lo digo antes de empezar.</p>
        </div>
        <Photo src="/assets/modelo-dental-project-bm.jpg" alt="Modelo con puente anterior de cerámica, grabado Dental Project BM" ratio="4 / 3"/>
      </div>
    </Section>
    <Section tone="card" style={{paddingTop:56}}>
      <Tabs items={[{value:"materiales",label:"Materiales y plazos"},{value:"flujo",label:"Qué necesito de ti"},{value:"envios",label:"Envíos"}]} value={tab} onChange={setTab}/>
      {tab==="materiales"&&(
        <div style={{marginTop:32,border:"1px solid var(--line-default)",borderRadius:"var(--radius-3)",overflow:"hidden"}}>
          <div className="dp-row3 dp-hidesm" style={{padding:"12px 20px",background:"var(--surface-sunken)",borderBottom:"1px solid var(--line-default)"}}>
            {["Material","Indicación","Plazo"].map(h=><span key={h} style={{fontFamily:"var(--font-display)",fontSize:11,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:"var(--fg-3)"}}>{h}</span>)}
          </div>
          {MATERIALS.map(([m,i,p],idx)=>(
            <div key={m} className="dp-row3" style={{padding:"16px 20px",alignItems:"center",borderBottom:idx<MATERIALS.length-1?"1px solid var(--line-hairline)":"none",background:"var(--surface-card)"}}>
              <span style={{fontFamily:"var(--font-display)",fontWeight:600,fontSize:15}}><Glitch>{m}</Glitch></span>
              <span style={{fontSize:14,color:"var(--fg-2)"}}>{i}</span>
              <span style={{fontFamily:"var(--font-mono)",fontSize:13}}>{p}</span>
            </div>
          ))}
        </div>
      )}
      {tab==="flujo"&&(
        <div className="dp-g3" style={{marginTop:32,gap:24}}>
          {[["file-box","El archivo","STL o el fichero nativo del intraoral. Antagonista incluido."],["clipboard-list","La indicación","Material, color y si hay algo que quieras revisar antes."],["calendar","La fecha","Cuándo lo necesitas en clínica; te confirmo si entra."]].map(([ic,t,d])=>(
            <Card key={t} pad="lg"><span style={{color:"var(--cyan-700)"}}><Icon name={ic} size={22} strokeAlign="middle"/></span>
              <h4 style={{marginTop:14}}><Glitch>{t}</Glitch></h4><p style={{fontSize:14,lineHeight:1.6,color:"var(--fg-2)",marginTop:8}}>{d}</p></Card>
          ))}
        </div>
      )}
      {tab==="envios"&&(
        <div style={{marginTop:32,display:"flex",flexWrap:"wrap",gap:24}}>
          <Card pad="lg" style={{flex:"1 1 260px"}}><Badge tone="brand">Jerez y alrededores</Badge><h4 style={{marginTop:14}}>Recogida y entrega propia</h4><p style={{fontSize:14,color:"var(--fg-2)",marginTop:8,lineHeight:1.6}}>Paso yo por la clínica. Sin coste añadido.</p></Card>
          <Card pad="lg" style={{flex:"1 1 260px"}}><Badge>Resto de España</Badge><h4 style={{marginTop:14}}>Mensajería 24 h</h4><p style={{fontSize:14,color:"var(--fg-2)",marginTop:8,lineHeight:1.6}}>Salida diaria a las 18:00 con número de seguimiento en el albarán.</p></Card>
        </div>
      )}
    </Section>
  </>;
}

/* ---------- Agentes IA ---------- */
const AGENTS=[
  ["inbox","Recepción de casos","Lee el correo y el WhatsApp, guarda los STL y crea el caso con la clínica ya asignada."],
  ["layers","Laminado","Prepara el archivo para la impresora con el perfil correcto y lo deja en cola."],
  ["bell","Avisos a clínica","Manda el estado del caso cuando cambia de fase, sin que yo escriba nada."],
  ["file-text","Albaranes","Rellena el albarán y lo archiva junto al caso."]
];

const GUIDE_FILE = "/assets/dental-project-guia-automatizacion.pdf";

function GuideDownload(){
  const [status,setStatus]=React.useState("idle"); // idle | sending | done | error
  const [error,setError]=React.useState("");

  async function handleSubmit(e){
    e.preventDefault();
    const form=e.currentTarget;
    const data=new FormData(form);
    // Honeypot: si un bot rellenó el campo oculto, fingimos éxito sin mandar nada ni abrir el PDF.
    if(data.get("website")){ setStatus("done"); return; }

    setStatus("sending");
    setError("");
    try{
      const res=await fetch("/api/guide-lead",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          nombre:data.get("nombre"),
          laboratorio:data.get("laboratorio"),
          email:data.get("email"),
          website:data.get("website")
        })
      });
      const body=await res.json().catch(()=>({}));
      if(!res.ok) throw new Error(body.error||"No se pudo procesar la descarga. Prueba otra vez.");
      setStatus("done");
      window.open(GUIDE_FILE,"_blank","noopener");
      form.reset();
    }catch(err){
      setError(err.message||"No se pudo procesar la descarga. Prueba otra vez.");
      setStatus("error");
    }
  }

  return (
    <Section tone="brand">
      <Reveal>
        <div style={{textAlign:"center",maxWidth:560,margin:"0 auto"}}>
          <span style={{display:"inline-flex",color:"var(--cyan-400)"}}><Icon name="file-text" size={30} strokeAlign="middle"/></span>
          <div style={{marginTop:14,display:"flex",justifyContent:"center"}}><Badge tone="success" dot>PDF gratis</Badge></div>
          <h2 style={{fontSize:H2,marginTop:14,color:"#fff"}}>La guía <Glitch loud>GRATIS</Glitch> para conectar tu impresora con la IA</h2>
          <p style={{fontSize:16,lineHeight:1.6,color:"var(--neutral-200)",marginTop:14,maxWidth:"46ch",marginLeft:"auto",marginRight:"auto"}}>
            Paso a paso, cómo vinculé Claude con Photon Workshop y mi impresora 3D para que prepare e imprima los modelos sola. Sin programar, sin coste.
          </p>
        </div>
        <Card pad="lg" style={{maxWidth:640,margin:"32px auto 0"}}>
          <form onSubmit={handleSubmit} style={{display:"grid",gap:14}}>
            <div aria-hidden="true" style={{position:"absolute",left:"-9999px",width:1,height:1,overflow:"hidden"}}>
              <label>No rellenar<input type="text" name="website" tabIndex={-1} autoComplete="off"/></label>
            </div>
            <div className="dp-g3" style={{gap:14}}>
              <Input name="nombre" label="Tu nombre" placeholder="Nombre y apellidos" required maxLength={100} disabled={status==="sending"}/>
              <Input name="laboratorio" label="Laboratorio" placeholder="Nombre del laboratorio" required maxLength={150} disabled={status==="sending"}/>
              <Input name="email" label="Email" placeholder="tu@laboratorio.es" type="email" required maxLength={200} disabled={status==="sending"}/>
            </div>
            {status==="error"&&<p role="alert" style={{margin:0,fontSize:13,color:"var(--text-danger)"}}>{error}</p>}
            <div style={{display:"flex",justifyContent:"center",marginTop:4}}>
              <Button size="lg" variant="accent" type="submit" disabled={status==="sending"} iconRight={<Icon name="arrow-right" size={16}/>}>
                {status==="sending"?"Un momento…":"Descargar la guía"}
              </Button>
            </div>
          </form>
        </Card>
      </Reveal>
    </Section>
  );
}

export function AgentsScreen({go}){
  const [flow,setFlow]=React.useState(null);
  return <>
    <Section tone="brand" style={{paddingTop:88}}>
      <div className="dp-split" style={{gap:"clamp(32px,5vw,64px)",alignItems:"center"}}>
        <div>
      <Eyebrow invert>Automatización para laboratorios</Eyebrow>
      <h1 style={{fontSize:"clamp(32px,5.4vw,48px)",marginTop:18,maxWidth:"22ch",color:"#fff"}}>Monté <Glitch>Agentes de IA</Glitch> para dejar de perder horas en el ordenador</h1>
      <p style={{fontSize:"clamp(17px,2vw,20px)",lineHeight:1.45,color:"var(--neutral-200)",marginTop:20,maxWidth:"52ch"}}>
        Soy protésico, no programador. Empecé automatizando lo que más me quemaba y hoy tengo cuatro Agentes trabajando en el laboratorio. Esto es lo que hacen y cómo los montarías tú.
      </p>
        </div>
        <Photo src="/assets/radiografia-implantes.jpg" alt="Radiografía con tres implantes en el maxilar inferior" ratio="4 / 3" style={{border:"1px solid var(--line-on-brand)"}}/>
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:"24px 32px",marginTop:44,paddingTop:24,borderTop:"1px solid var(--line-on-brand)"}}>
        {[["4","Agentes en marcha"],["~40 min","al día recuperados"],["0","casos perdidos por papeleo"]].map(([n,l])=>(
          <div key={l}><div style={{fontFamily:"var(--font-mono)",fontSize:26,color:"var(--cyan-400)"}}>{n}</div><div style={{fontSize:13,color:"var(--neutral-300)",marginTop:2}}>{l}</div></div>
        ))}
      </div>
    </Section>
    <Section tone="card">
      <AgentDiagram/>
    </Section>
    <Section>
      <Reveal><Eyebrow>Los Agentes</Eyebrow>
      <h2 style={{fontSize:H2,marginTop:16}}><Glitch>Qué automatizo hoy</Glitch></h2></Reveal>
      <div className="dp-g2" style={{gap:24,marginTop:40}}>
        {AGENTS.map(([ic,t,d],n)=>(
          <Reveal key={t} dir={n%2===0?"left":"right"} delay={(n>>1)*80}><Card pad="lg" interactive style={{height:"100%",cursor:"pointer"}} role="button" tabIndex={0}
            onClick={()=>setFlow(t)} onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();setFlow(t)}}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <span style={{color:"var(--cyan-700)"}}><Icon name={ic} size={20} strokeAlign="middle"/></span>
              <h4 style={{flex:1}}>{t}</h4><Badge tone="success" dot>Activo</Badge>
            </div>
            <p style={{fontSize:14,lineHeight:1.6,color:"var(--fg-2)",marginTop:12}}>{d}</p>
            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:14,fontFamily:"var(--font-mono)",fontSize:11,color:"var(--cyan-700)"}}>
              Ver el proceso<Icon name="arrow-right" size={13} strokeAlign="middle"/>
            </div>
          </Card></Reveal>
        ))}
      </div>
    </Section>
    <Section tone="card">
      <div className="dp-g2" style={{gap:"clamp(40px,5vw,64px)",alignItems:"center"}}>
        <Reveal><Eyebrow>Si tienes un laboratorio pequeño</Eyebrow>
          <h2 style={{fontSize:H2,marginTop:16,maxWidth:"18ch"}}><Glitch>Te ayudo a montarlo en el tuyo</Glitch></h2>
          <p style={{fontSize:17,lineHeight:1.6,color:"var(--fg-2)",marginTop:18,maxWidth:"46ch"}}>Miramos juntos por dónde se te va el tiempo, elegimos un proceso y lo dejamos funcionando. Nada de plataformas gigantes: herramientas sencillas sobre lo que ya usas.</p>
          <div style={{marginTop:26}}><Button size="lg" onClick={()=>go("contacto")}>Hablar con Borja</Button></div>
        </Reveal>
        <Photo src="/assets/taller-flujo-digital.jpg" alt="Puesto de trabajo digital: escáner, diseño CAD en pantalla e impresora de resina" ratio="16 / 11" fit="contain"/>
      </div>
    </Section>
    <GuideDownload/>
    <AgentFlowModal name={flow} onClose={()=>setFlow(null)}/>
  </>;
}

/* ---------- Contacto ---------- */
const CASE_TYPES=["Corona o puente","Férula","Sobre implante","Modelos impresos","Automatización / Agentes"];

export function ContactScreen(){
  const [status,setStatus]=React.useState("idle"); // idle | sending | sent | error
  const [error,setError]=React.useState("");

  async function handleSubmit(e){
    e.preventDefault();
    const form=e.currentTarget;
    const data=new FormData(form);
    // Honeypot: si un bot rellenó el campo oculto, fingimos éxito sin mandar nada.
    if(data.get("website")){ setStatus("sent"); return; }
    if(!data.get("consent")){ setError("Marca la casilla para poder escribirte."); setStatus("error"); return; }

    setStatus("sending");
    setError("");
    try{
      const res=await fetch("/api/contact",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          nombre:data.get("nombre"),
          clinica:data.get("clinica"),
          email:data.get("email"),
          tipo:data.get("tipo"),
          archivo:data.get("archivo"),
          mensaje:data.get("mensaje"),
          website:data.get("website")
        })
      });
      const body=await res.json().catch(()=>({}));
      if(!res.ok) throw new Error(body.error||"No se pudo enviar. Prueba otra vez o escribe directamente a dentalproject404@gmail.com.");
      setStatus("sent");
      form.reset();
    }catch(err){
      setError(err.message||"No se pudo enviar. Prueba otra vez o escribe directamente a dentalproject404@gmail.com.");
      setStatus("error");
    }
  }

  return <>
    <Section tone="card" style={{paddingTop:72}}>
      <div className="dp-g2" style={{gap:"clamp(40px,5vw,64px)"}}>
        <div>
          <Eyebrow>Contacto</Eyebrow>
          <h1 style={{fontSize:H2,marginTop:16,maxWidth:"18ch"}}><Glitch>Cuéntame el caso. Laboratorio dental en Jerez de la Frontera</Glitch></h1>
          <p style={{fontSize:17,lineHeight:1.6,color:"var(--fg-2)",marginTop:16,maxWidth:"40ch"}}>Contesto el mismo día. Si es urgente, llámame directamente.</p>
          <div style={{marginTop:32,display:"flex",flexDirection:"column",gap:14}}>
            {[["mail","dentalproject404@gmail.com"],["phone","+34 666 828 334"],["map-pin","Jerez de la Frontera, Cádiz"],["clock","L-V · 9:00 a 18:00"]].map(([ic,t])=>(
              <div key={t} style={{display:"flex",alignItems:"center",gap:12}}>
                <span style={{color:"var(--cyan-700)"}}><Icon name={ic} size={17} strokeAlign="middle"/></span>
                <span style={{fontSize:15,color:"var(--fg-1)"}}>{t}</span>
              </div>
            ))}
          </div>
        </div>
        <Card pad="lg">
          <form onSubmit={handleSubmit} style={{display:"grid",gap:16}}>
            {/* Honeypot: oculto para personas, visible para bots que rellenan formularios a ciegas. */}
            <div aria-hidden="true" style={{position:"absolute",left:"-9999px",width:1,height:1,overflow:"hidden"}}>
              <label>No rellenar<input type="text" name="website" tabIndex={-1} autoComplete="off"/></label>
            </div>
            <div className="dp-form2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <Input name="nombre" label="Tu nombre" placeholder="Nombre y apellidos" required maxLength={100} disabled={status==="sending"}/>
              <Input name="clinica" label="Clínica" placeholder="Nombre de la clínica" required maxLength={150} disabled={status==="sending"}/>
            </div>
            <div className="dp-form2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <Input name="email" label="Email" placeholder="tu@clinica.es" type="email" required maxLength={200} disabled={status==="sending"}/>
              <Select name="tipo" label="Tipo de caso" options={CASE_TYPES} disabled={status==="sending"}/>
            </div>
            <Input name="archivo" label="Archivo del caso" placeholder="Pega un enlace al STL" maxLength={300} prefix={<Icon name="paperclip" size={15} strokeAlign="middle"/>} hint="También puedes mandarlo por correo después." disabled={status==="sending"}/>
            <label style={{display:"block"}}>
              <span style={{display:"block",marginBottom:6,fontFamily:"var(--font-display)",fontSize:13,fontWeight:600,letterSpacing:".04em"}}>Cuéntame</span>
              <textarea name="mensaje" rows={4} maxLength={2000} placeholder="Material, color, fecha en la que lo necesitas..." disabled={status==="sending"} style={{width:"100%",padding:"10px 12px",fontFamily:"var(--font-body)",fontSize:14,color:"var(--fg-1)",background:"var(--surface-card)",border:"1px solid var(--line-default)",borderRadius:"var(--radius-control)",resize:"vertical"}}/>
            </label>
            <Checkbox name="consent" required label="Acepto que Borja me escriba para hablar del caso"/>
            {status==="error"&&<p role="alert" style={{margin:0,fontSize:13,color:"var(--text-danger)"}}>{error}</p>}
            <div style={{display:"flex",gap:12,alignItems:"center"}}>
              <Button size="lg" type="submit" disabled={status==="sending"}>{status==="sending"?"Enviando…":"Enviar"}</Button>
              <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--fg-3)"}}>respuesta el mismo día</span>
            </div>
          </form>
        </Card>
      </div>
    </Section>
    {status==="sent"&&<div style={{position:"fixed",right:"max(12px,3vw)",left:"max(12px,3vw)",bottom:20,zIndex:80,display:"flex",justifyContent:"flex-end"}}>
      <Toast tone="success" title="Caso enviado" message="Te contesto hoy mismo con el plazo." onClose={()=>setStatus("idle")}/>
    </div>}
  </>;
}
